import React, { useCallback } from 'react';
import cn from 'classnames';
import { PassengerFragment } from '@websky/graphql';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import { getTravellerName } from '../../utils';

import { WarningRounded } from '../../Icons';
import { Field, Form } from 'react-final-form';
import { ModalX, BaseButton, Input } from '../../BaseComponents';
import { IINFieldKeyFactory } from '../utils';

export type InsuranceIINModalValues = {
	[key: string]: string;
};

interface Props {
	passengers: PassengerFragment[];
	onClose: React.DispatchWithoutAction;
	isOpen: boolean;
	isSubmitLoading: boolean;
	onSubmit: (values: InsuranceIINModalValues) => Promise<void>;
	className?: string;
}

const InsuranceModalConfirmINN: React.FC<Props> = ({
	className,
	passengers,
	isOpen,
	onClose,
	isSubmitLoading,
	onSubmit
}) => {
	const { t } = useTranslation('InsuranceModalConfirmINN');
	const theme = useTheme('InsuranceModalConfirmINN').InsuranceModalConfirmINN;

	const validate = useCallback((value: string) => {
		if (!value) {
			return t('Required');
		} else if (value.length < 12) {
			return t('Enter 12 digits');
		}

		return undefined;
	}, []);

	return (
		<ModalX
			isOpen={isOpen}
			onClose={onClose}
			title={t('Enter your IIN for insurance')}
			size="large"
			classes={{ paper: cn(theme.modal, className), closeButton: theme.closeButton, title: theme.title }}
		>
			<h3 className={theme.subtitle}>
				{t(
					'In order to activate the insurance policy it is necessary to specify the IIN of each passenger. This is necessary to apply for and receive payment in case of accidents covered by the insurance.'
				)}
			</h3>

			<Form
				onSubmit={onSubmit}
				render={({ handleSubmit, submitErrors }) => (
					<form onSubmit={handleSubmit} className={theme.passengers}>
						{passengers.map(passenger => (
							<div className={theme.passenger} key={passenger.id}>
								<div className={theme.passenger__name}>{getTravellerName(passenger)}</div>

								<Field
									name={IINFieldKeyFactory(passenger.id)}
									validate={validate}
									render={({ input, meta }) => (
										<Input
											{...input}
											className={theme.passenger__input}
											placeholder={t('Passenger IIN')}
											label={t('Passenger IIN')}
											error={
												(meta.error && meta.touched) ||
												(!meta.dirtySinceLastSubmit && !!submitErrors)
											}
											helperText={
												(!!meta.error && meta.touched && meta.error) ||
												(!meta.dirtySinceLastSubmit && t(submitErrors))
											}
											inputProps={{
												maxLength: 12
											}}
										/>
									)}
								/>
							</div>
						))}

						<p className={theme.info}>
							<span className={theme.info__icon}>{WarningRounded}</span>
							{t('IIN is an Individual Identification Number that consists of 12 numbers')}
						</p>

						<div className={theme.controls}>
							<BaseButton onClick={onClose} variant="smallSecondary" className={theme.controls__cancel}>
								{t('Cancel')}
							</BaseButton>
							<BaseButton
								type="submit"
								variant="smallPrimary"
								className={theme.controls__continue}
								isLoading={isSubmitLoading}
							>
								{t('Continue')}
							</BaseButton>
						</div>
					</form>
				)}
			/>
		</ModalX>
	);
};

export default InsuranceModalConfirmINN;
